import * as moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { toast } from "react-toastify";
import { Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";
import { Button, Col, Row, RSelect } from "../../../components/Component";
import { GET, POST } from "../../../helper/api";
import { BILL_TYPE, INGREDIENT_CATEGORY_NAME } from "../../../utils/Utils";

const SHIFT_OPTIONS = [
  { label: "Sáng", value: "SÁNG" },
  { label: "Trưa", value: "TRƯA" },
  { label: "Chiều", value: "CHIỀU" },
  { label: "Cả ngày", value: "CẢ NGÀY" },
];

const DIRECTION_OPTIONS = ["IN", "OUT"].map((value) => ({ label: BILL_TYPE[value], value }));

const INGREDIENT_OPTIONS = Object.entries(INGREDIENT_CATEGORY_NAME).map(([value, label]) => ({ label, value }));

const DEFAULT_VALUES = {
  startDate: moment().format("YYYY-MM-DD"),
  startShift: null,
  ingredientId: null,
  customerDirection: null,
  customerId: null,
  fromLocation: null,
  toLocation: null,
  weight: 0,
  driveRentalContractId: null,
  note: "",
};

const CreatePNRequestBillModal = ({ isOpen, toggle, onCreated, pnCustomers, locationDistances }) => {
  const [submitting, setSubmitting] = useState(false);
  const [driveRentalContractOptions, setDriveRentalContractOptions] = useState([]);

  const { control, handleSubmit, reset, register, formState: { errors } } = useForm({
    defaultValues: DEFAULT_VALUES,
  });

  useEffect(() => {
    if (!isOpen) return;
    reset(DEFAULT_VALUES);
    GET("/drive-rental-contract", { status: "INIT" }, true).then(({ data }) => {
      setDriveRentalContractOptions((data?.rows || []).map((c) => ({
        label: `${c?.customer?.name} - HĐ số ${c.id} - Từ "${c?.info?.from}" đến "${c?.info?.to}"`,
        value: c.id,
      })));
    });
  }, [isOpen]);

  const customerOptions = useMemo(
    () => pnCustomers.map((c) => ({ label: c.name, value: c.id })),
    [pnCustomers],
  );

  const [fromLocationOptions, toLocationOptions] = useMemo(() => {
    const fromObj = {};
    const toObj = {};
    locationDistances.forEach((d) => {
      if (d.fromLocation) fromObj[d.fromLocation] = d.fromLocationName;
      if (d.toLocation) toObj[d.toLocation] = d.toLocationName;
    });
    const toOptions = (obj) => Object.keys(obj).map((value) => ({ label: obj[value] || value, value }));
    return [toOptions(fromObj), toOptions(toObj)];
  }, [locationDistances]);

  const onSubmit = (values) => {
    setSubmitting(true);
    const payload = {
      ...values,
      ingredientVATName: INGREDIENT_CATEGORY_NAME[values.ingredientId],
      fromLocationName: fromLocationOptions.find((o) => o.value === values.fromLocation)?.label,
      toLocationName: toLocationOptions.find((o) => o.value === values.toLocation)?.label,
      driveRentalContractId: values.driveRentalContractId || undefined,
      note: values.note?.trim() || undefined,
    };
    POST("/pn/pn-request-bill/web", payload, {}, true)
      .then(({ data }) => {
        toast.success("Đã tạo phiếu điều xe");
        toggle();
        onCreated(data);
      })
      .catch((err) => toast.error(err?.response?.data?.error || "Có lỗi xảy ra"))
      .finally(() => setSubmitting(false));
  };

  const renderSelect = (name, label, options, { required = true, ...props } = {}) => (
    <div className="form-group">
      <label className="form-label">
        {label} {required && <span className="text-danger">*</span>}
      </label>
      <Controller
        control={control}
        name={name}
        rules={required ? { required: "Bắt buộc" } : {}}
        render={({ onChange, value }) => (
          <RSelect
            options={options}
            value={options.find((o) => o.value === value) || null}
            onChange={(opt) => onChange(opt?.value ?? null)}
            {...props}
          />
        )}
      />
      {errors?.[name] && <small className="text-danger">{errors[name].message}</small>}
    </div>
  );

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" className="modal-dialog-centered">
      <ModalHeader toggle={toggle}>Tạo phiếu điều xe</ModalHeader>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalBody>
          <Row className="g-2">
            <Col md={4}>
              <div className="form-group">
                <label className="form-label">Ngày <span className="text-danger">*</span></label>
                <input
                  type="date"
                  className="form-control"
                  name="startDate"
                  ref={register({ required: "Bắt buộc" })}
                />
                {errors?.startDate && <small className="text-danger">{errors.startDate.message}</small>}
              </div>
            </Col>
            <Col md={4}>{renderSelect("startShift", "Buổi", SHIFT_OPTIONS, { placeholder: "Chọn buổi..." })}</Col>
            <Col md={4}>{renderSelect("customerDirection", "Chiều H.Hoá", DIRECTION_OPTIONS, { placeholder: "Nhập / Xuất" })}</Col>
            <Col md={4}>{renderSelect("ingredientId", "Hàng hoá", INGREDIENT_OPTIONS, { placeholder: "Chọn hàng hoá..." })}</Col>
            <Col md={4}>{renderSelect("customerId", "Công nợ", customerOptions, { placeholder: "Chọn công nợ..." })}</Col>
            <Col md={4}>
              <div className="form-group">
                <label className="form-label">Số lượng (kg) <span className="text-danger">*</span></label>
                <Controller
                  control={control}
                  name="weight"
                  rules={{ required: "Bắt buộc", min: { value: 1, message: "Tối thiểu 1" } }}
                  render={({ onChange, value }) => (
                    <NumericFormat
                      className="form-control"
                      value={value}
                      thousandSeparator=","
                      onValueChange={({ floatValue }) => onChange(floatValue || 0)}
                      placeholder="0"
                    />
                  )}
                />
                {errors?.weight && <small className="text-danger">{errors.weight.message}</small>}
              </div>
            </Col>
            <Col md={6}>{renderSelect("fromLocation", "Điểm A (Kho)", fromLocationOptions, { placeholder: "Chọn điểm A..." })}</Col>
            <Col md={6}>{renderSelect("toLocation", "Điểm B", toLocationOptions, { placeholder: "Chọn điểm B..." })}</Col>
            <Col md={12}>
              {renderSelect("driveRentalContractId", "Hợp đồng thuê xe", driveRentalContractOptions, {
                required: false,
                isClearable: true,
                placeholder: "Không có...",
              })}
            </Col>
            <Col md={12}>
              <div className="form-group">
                <label className="form-label">Ghi chú</label>
                <textarea className="form-control" rows={2} name="note" ref={register()} />
              </div>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" type="submit" disabled={submitting}>
            {submitting ? <Spinner size="sm" color="light" className="mr-1" /> : null}
            Tạo phiếu
          </Button>
          <Button color="light" type="button" onClick={toggle}>
            Huỷ
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default CreatePNRequestBillModal;
